import { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { syncAll, getPendingCount } from '@/utils/offlineCache';
import { motion, AnimatePresence } from 'framer-motion';

export function OfflineBanner({ api }) {
  const [offline, setOffline] = useState(!navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const goOffline = () => { setOffline(true); setSyncResult(null); };
    const goOnline = () => {
      setOffline(false);
      const count = getPendingCount();
      if (count > 0 && api) {
        setSyncing(true);
        setPendingCount(count);
        syncAll(api).then((result) => {
          setSyncing(false);
          setSyncResult(result);
          setPendingCount(getPendingCount());
          if (result.failed === 0) {
            setTimeout(() => setSyncResult(null), 3000);
          }
        });
      }
    };

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    setPendingCount(getPendingCount());

    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, [api]);

  const isVisible = offline || syncing || (syncResult && (syncResult.failed > 0 || syncResult.exercises + syncResult.sessions > 0));
  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed top-0 left-0 right-0 z-[60]"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className={`mx-3 mt-2 px-4 py-3 rounded-xl backdrop-blur-md border flex items-center justify-center gap-2.5 text-sm font-heading font-bold uppercase tracking-wider ${
            offline
              ? 'bg-[#1A1A1A]/95 border-white/10 text-white/80'
              : syncing
              ? 'bg-[#1A1A1A]/95 border-accent/20 text-accent'
              : syncResult?.failed > 0
              ? 'bg-[#1A1A1A]/95 border-red-500/20 text-red-400'
              : 'bg-[#1A1A1A]/95 border-accent/20 text-accent'
          }`} data-testid="offline-banner">
            {offline ? (
              <>
                <WifiOff size={15} className="text-white/50" />
                <span>Offline Mode</span>
                <span className="text-white/40 font-normal normal-case tracking-normal">— data saved locally</span>
              </>
            ) : syncing ? (
              <>
                <RefreshCw size={15} className="animate-spin" />
                <span>Syncing {pendingCount} items</span>
              </>
            ) : syncResult?.failed > 0 ? (
              <>
                <AlertTriangle size={15} />
                <span>{syncResult.failed} items failed to sync</span>
              </>
            ) : syncResult ? (
              <>
                <Check size={15} />
                <span>All data synced</span>
              </>
            ) : null}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
